import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../contexts/AlertContext';
import { useLanguage } from '../contexts/LanguageContext';
import './QuizPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const QuizPage = () => {
  const { bookId, difficulty } = useParams(); // v2: difficulty는 이제 set_id (SET1_FOUNDATION, etc.)
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning, showInfo, showModal, showConfirm } = useAlert();
  const { currentLanguage } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({}); // v2: 로컬에 저장 후 일괄 제출
  const [assessmentId, setAssessmentId] = useState(null); // v2: UUID
  const [referenceGroupId, setReferenceGroupId] = useState(null); // v2: 규준 그룹
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [bookInfo, setBookInfo] = useState(null);
  const [levelTag, setLevelTag] = useState(null); // 레벨 태그 (L1, L2, L3, L4, L5, L6)
  
  // 힌트 시스템 상태
  const [hintsUsed, setHintsUsed] = useState({}); // {questionId: [1, 2, 3]}
  const [visibleHints, setVisibleHints] = useState({}); // {questionId: {1: true, 2: false, 3: false}}
  
  // 시간 측정 상태
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [timeTaken, setTimeTaken] = useState({}); // {questionId: seconds}
  
  const timerRef = useRef(null);

  useEffect(() => {
    // 로그인 상태를 먼저 확인
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const user = localStorage.getItem('user');
    
    // URL에서 파라미터 가져오기
    const urlParams = new URLSearchParams(window.location.search);
    const student = urlParams.get('student');
    const guest = urlParams.get('guest');
    const levelTagParam = urlParams.get('level'); // 레벨 태그 (L1, L2, L3, L4, L5, L6)
    setLevelTag(levelTagParam); // 상태에 저장
    
    if (token && userType === 'student' && user) {
      // 로그인한 학생은 URL 파라미터와 관계없이 정상 모드로 처리
      const userData = JSON.parse(user);
      setStudentId(userData.id);
      startQuiz(userData.id, levelTagParam);
    } else if (student) {
      // 특정 학생 ID가 지정된 경우
      setStudentId(parseInt(student));
      startQuiz(parseInt(student), levelTagParam);
    } else {
      // 로그인하지 않은 경우만 게스트 모드
      setIsGuestMode(true);
      startGuestQuiz(levelTagParam);
    }
  }, [bookId, difficulty]);

  useEffect(() => {
    // 문제 시작 시간 기록
    if (questions.length > 0 && !showFeedback) {
      setQuestionStartTime(Date.now());
      setCurrentTime(Date.now());
    }
  }, [currentQuestion, questions.length, showFeedback]);

  // 타이머를 실시간으로 업데이트
  useEffect(() => {
    if (questionStartTime && !showFeedback) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [questionStartTime, showFeedback]);

  const findStudentByEmail = async (email) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/students/email/${email}`);
      setStudentId(response.data.id);
      startQuiz(response.data.id);
    } catch (error) {
      console.error('Error finding student:', error);
      navigate('/students');
    }
  };

  const fetchBookInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/books/${bookId}?language=${currentLanguage}`);
      setBookInfo(response.data);
    } catch (error) {
      console.error('Error fetching book info:', error);
    }
  };

  const startQuiz = async (studentId, levelTag = null) => {
    try {
      // 책 정보도 함께 불러오기
      await fetchBookInfo();
      
      // v2: 사용자 정보에서 grade_level과 country 가져오기
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // grade_level 정수 변환 (문자열 처리)
      let gradeLevel = 4; // 기본값 4
      if (user.grade) {
        const gradeValue = user.grade;
        if (typeof gradeValue === 'number') {
          gradeLevel = gradeValue;
        } else if (typeof gradeValue === 'string') {
          // 숫자 문자열인 경우
          const parsed = parseInt(gradeValue);
          if (!isNaN(parsed)) {
            gradeLevel = parsed;
          } else {
            // 한글 문자열인 경우 매핑
            const gradeMap = {
              '초등': 4,
              '중등': 8,
              '고등': 12,
              '초': 4,
              '중': 8,
              '고': 12
            };
            gradeLevel = gradeMap[gradeValue] || 4;
          }
        }
      }
      
      const country = user.country || 'KR'; // 기본값 KR
      
      const requestBody = {
        student_id: studentId,
        book_id: parseInt(bookId),
        set_id: difficulty, // v2: difficulty는 이제 set_id (SET1_FOUNDATION, etc.)
        language: currentLanguage,
        grade_level: gradeLevel, // 정수로 변환된 값
        country: country
      };
      
      // level_tag가 있으면 추가 (null이나 undefined는 제외)
      if (levelTag && levelTag.trim() !== '') {
        requestBody.level_tag = levelTag;
      }
      
      // 디버깅 로그
      console.log('[QuizPage] Starting quiz with params:', requestBody);
      
      const response = await axios.post(`${API_BASE_URL}/api/v2/assessments/start`, requestBody);
      
      setQuestions(response.data.questions);
      setAssessmentId(response.data.assessment_id); // v2: UUID
      setReferenceGroupId(response.data.reference_group_id); // v2: 규준 그룹
      setLoading(false);
    } catch (error) {
      console.error('Error starting assessment:', error);
      showError(t('quiz.languageNotAvailable', { language: currentLanguage }));
      setLoading(false);
    }
  };

  const startGuestQuiz = async (levelTag = null) => {
    try {
      // 책 정보도 함께 불러오기
      await fetchBookInfo();
      
      // v2: 게스트도 기본 정보 제공
      const gradeLevel = 4; // 게스트 기본값
      const country = 'KR'; // 게스트 기본값
      
      const requestBody = {
        student_id: null, // 게스트는 null
        book_id: parseInt(bookId),
        set_id: difficulty, // v2: set_id
        language: currentLanguage,
        grade_level: gradeLevel,
        country: country
      };
      
      // level_tag가 있으면 추가 (null이나 undefined는 제외)
      if (levelTag && levelTag.trim() !== '') {
        requestBody.level_tag = levelTag;
      }
      
      // 디버깅 로그
      console.log('[QuizPage] Starting guest quiz with params:', requestBody);
      
      const response = await axios.post(`${API_BASE_URL}/api/v2/assessments/start`, requestBody);
      
      setQuestions(response.data.questions);
      setAssessmentId(response.data.assessment_id); // v2: UUID
      setReferenceGroupId(response.data.reference_group_id);
      setLoading(false);
    } catch (error) {
      console.error('Error starting guest assessment:', error);
      showError(t('quiz.languageNotAvailable', { language: currentLanguage }));
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const showHint = (questionId, hintNumber) => {
    const currentHints = hintsUsed[questionId] || [];
    const currentVisible = visibleHints[questionId] || {};
    
    if (!currentHints.includes(hintNumber)) {
      setHintsUsed({
        ...hintsUsed,
        [questionId]: [...currentHints, hintNumber]
      });
    }
    
    setVisibleHints({
      ...visibleHints,
      [questionId]: {
        ...currentVisible,
        [hintNumber]: true
      }
    });
  };

  // v2: 개별 답변 제출 제거 (로컬 저장만)
  const saveAnswerLocally = (questionId, answer, essayAnswer = null) => {
    const timeSpent = questionStartTime ? Math.floor((Date.now() - questionStartTime) / 1000) : 0;
    
    // 시간 기록 저장
    setTimeTaken(prev => ({
      ...prev,
      [questionId]: timeSpent
    }));
    
    // 답변은 이미 handleAnswer()에서 저장됨
    // 추가 로직이 필요하면 여기에 작성
  };

  const checkAnswer = () => {
    const question = questions[currentQuestion];
    const questionId = question.question_id || question.id;
    const answer = answers[questionId];
    
    if (!answer) {
      showWarning(t('quiz.pleaseEnterAnswer'));
      return;
    }

    setCurrentAnswer(answer);
    
    // 객관식 문항의 경우 정답 체크
    if (question.question_type === 'multiple_choice') {
      const correct = answer === question.correct_answer;
      setIsCorrect(correct);
    } else {
      // 주관식은 일단 정답으로 처리 (나중에 AI 평가 가능)
      setIsCorrect(true);
    }
    
    setShowFeedback(true);
  };

  const nextQuestion = () => {
    const question = questions[currentQuestion];
    const questionId = question.question_id || question.id;
    const answer = answers[questionId];
    
    // v2: 로컬 저장만 (서버 제출 제거)
    saveAnswerLocally(questionId, answer);

    // 피드백 상태 초기화
    setShowFeedback(false);
    setCurrentAnswer(null);
    setIsCorrect(null);

    // 스크롤을 맨 위로 이동
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // 게임 완료
      finishQuiz();
    }
  };

  const handleAnswerSubmit = () => {
    const question = questions[currentQuestion];
    const questionId = question.question_id || question.id;
    const answer = answers[questionId];
    
    if (!answer || answer.trim() === '') {
      showWarning(t('quiz.pleaseEnterAnswer'));
      return;
    }
    
    // v2: 로컬 저장만
    saveAnswerLocally(questionId, answer);
  };

  const finishQuiz = async () => {
    try {
      // v2: 모든 답변을 한번에 제출
      const responses = questions.map(question => {
        const questionId = question.question_id || question.id;
        const userAnswer = answers[questionId];
        const timeSpent = timeTaken[questionId] || 0;
        
        return {
          question_id: question.question_id || question.id, // v2: UUID
          user_answer: userAnswer, // v2: 단일 필드 사용
          time_taken_sec: timeSpent
        };
      });

      if (!isGuestMode) {
        // v2: 일괄 제출
        await axios.post(`${API_BASE_URL}/api/v2/assessments/${assessmentId}/submit`, {
          responses: responses
        });
        
        // v2: 결과 페이지로 이동 (UUID 사용)
        navigate(`/results/${assessmentId}`);
      } else {
        // 게스트 모드는 일단 로컬 점수 계산으로 표시
        const scoreDetails = calculateDetailedScore();
        const scoreMessage = `🏆 ${t('results.score')}: ${scoreDetails.correctAnswers}/${scoreDetails.totalQuestions}\n` +
          `📊 ${t('results.accuracy')}: ${scoreDetails.accuracy}%\n` +
          `\n✨ ${t('common.finish')}!`;
        
        showConfirm(
          scoreMessage,
          () => navigate('/test'),
          null,
          t('results.title')
        );
      }
    } catch (error) {
      console.error('Error completing assessment:', error);
      showError(t('quiz.errorCompleting'));
    }
  };

  const calculateScore = () => {
    let score = 0;
    questions.forEach(question => {
      const questionId = question.question_id || question.id;
      if (question.question_type === 'multiple_choice') {
        const answer = answers[questionId];
        if (answer === question.correct_answer) {
          score++;
        }
      } else {
        // 주관식은 일단 정답으로 처리
        if (answers[questionId] && answers[questionId].trim()) {
          score++;
        }
      }
    });
    return score;
  };

  const calculateDetailedScore = () => {
    const totalQuestions = questions.length;
    const correctAnswers = calculateScore();
    
    // 기본 점수 (1000점 기준)
    const baseScore = (correctAnswers / totalQuestions) * 1000;
    
    // 힌트 사용 감점 계산
    let totalHintPenalty = 0;
    let totalTimeBonus = 0;
    
    questions.forEach(question => {
      const questionId = question.question_id || question.id;
      const questionHints = hintsUsed[questionId] || [];
      const timeSpent = timeTaken[questionId] || 0;
      const questionScore = 1000 / totalQuestions;
      
      // 힌트 감점 (5% per hint)
      totalHintPenalty += questionHints.length * questionScore * 0.05;
      
      // 시간 보너스 (10초 이하 + 10%)
      if (timeSpent > 0 && timeSpent <= 10 && answers[questionId]) {
        const isCorrect = question.question_type === 'multiple_choice' 
          ? answers[questionId] === question.correct_answer
          : (answers[questionId] && answers[questionId].trim());
        
        if (isCorrect) {
          totalTimeBonus += questionScore * 0.10;
        }
      }
    });
    
    const finalScore = Math.max(0, Math.min(1200, baseScore - totalHintPenalty + totalTimeBonus));
    
    return {
      correctAnswers,
      totalQuestions,
      baseScore: Math.round(baseScore),
      timeBonus: Math.round(totalTimeBonus),
      hintPenalty: Math.round(totalHintPenalty),
      finalScore: Math.round(finalScore),
      accuracy: Math.round((correctAnswers / totalQuestions) * 100)
    };
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getHintText = (question, hintNumber) => {
    // v2: hints 배열 사용
    if (question.hints && Array.isArray(question.hints)) {
      const hint = question.hints[hintNumber - 1];
      return hint && hint.trim() ? hint : null;
    }
    // v1 호환성
    switch(hintNumber) {
      case 1: return question.hint_1 || null;
      case 2: return question.hint_2 || null;
      case 3: return question.hint_3 || null;
      default: return question.hint || null;
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>{t('quiz.loadingQuestions')}</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="container">
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>{t('quiz.languageNotAvailable', { language: currentLanguage })}</h3>
          <p>{t('test.noBooksDescription')}</p>
          <button onClick={() => navigate('/test')} className="btn btn-primary">
            {t('test.viewAllBooks')}
          </button>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  // v2: question_id 사용, v1 호환성 유지
  const questionId = question.question_id || question.id;
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const questionHints = hintsUsed[questionId] || [];
  const visibleQuestionHints = visibleHints[questionId] || {};

  return (
    <div className="container quiz-container">
      {bookInfo && (
        <div className="book-title-container">
          <h2 className="current-book-title">{bookInfo.title}</h2>
        </div>
      )}
      <div className="quiz-header">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="quiz-meta">
          <span className="progress-text">
            {currentQuestion + 1} / {questions.length}
          </span>
          <span className="difficulty-badge difficulty-{difficulty}">
            {levelTag ? (
              // 레벨 태그가 있으면 레벨 표시 (L1 -> 레벨 1)
              `📚 ${t('test.level')} ${levelTag.replace('L', '')}`
            ) : (
              // 레벨 태그가 없으면 기존 단계 표시
              difficulty === 'PLACEMENT' ? `⚪ ${t('test.stage1')}` :
              difficulty === 'SET1_FOUNDATION' ? `🟢 ${t('test.stage2')}` : 
              difficulty === 'SET2_COMPREHENSION' ? `🟡 ${t('test.stage3')}` : 
              difficulty === 'SET3_TRANSFER' ? `🔴 ${t('test.stage4')}` :
              difficulty === 'SET4_STORY_TRACKING' ? `⚫ ${t('test.stage5')}` : `🔵 ${t('difficulty.DEMO')}`
            )}
          </span>
        </div>
      </div>

      <div className="card question-card">
        <div className="question-header">
          <div className="question-type-badge">
            {question.question_type === 'multiple_choice' ? `📝 ${t('common.multipleChoice') || 'Multiple Choice'}` : `✍️ ${t('common.essay') || 'Essay'}`}
          </div>
          {questionStartTime && (
            <div className="timer">
              {t('quiz.timer')} {formatTime(Math.floor((currentTime - questionStartTime) / 1000))}
            </div>
          )}
        </div>
        
        <h2 className="question-text">{question.question_text}</h2>

        {/* 힌트 시스템 */}
        <div className="hints-section">
          <div className="hints-header">
            <h4>💡 {t('quiz.hint').toUpperCase()} ({questionHints.length}/3)</h4>
          </div>
          <div className="hints-buttons">
            {[1, 2, 3].map(hintNum => {
              const hintText = getHintText(question, hintNum);
              const isUsed = questionHints.includes(hintNum);
              
              return (
                <button
                  key={hintNum}
                  className={`hint-button ${isUsed ? 'used' : ''} ${!hintText ? 'disabled' : ''}`}
                  onClick={() => showHint(questionId, hintNum)}
                  disabled={isUsed || !hintText}
                >
                  {isUsed ? '🔓' : '🔒'} {hintNum}
                  {!hintText && ' - ' + t('common.loading')}
                </button>
              );
            })}
          </div>
          
          {/* 힌트 콘텐츠 영역 */}
          <div className="hints-content-area">
            {[1, 2, 3].map(hintNum => {
              const hintText = getHintText(question, hintNum);
              const isVisible = visibleQuestionHints[hintNum];
              
              return isVisible && hintText && (
                <div key={hintNum} className="hint-content">
                  <span className="hint-icon">💡</span>
                  <span className="hint-label">{t('quiz.showHint', { number: hintNum })}:</span>
                  <span className="hint-text">
                    {hintText}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {!showFeedback ? (
          <>
            {question.question_type === 'multiple_choice' ? (
              <div className="options">
                {['A', 'B', 'C', 'D', 'E'].map(option => {
                  // v2: options 객체 사용, v1 호환성 유지
                  const optionText = question.options 
                    ? question.options[option] 
                    : question[`option_${option.toLowerCase()}`];
                  if (!optionText) return null;
                  
                  // v2: question_id 사용, v1 호환성 유지
                  const questionId = question.question_id || question.id;
                  
                  return (
                    <label key={option} className="option-label">
                      <input
                        type="radio"
                        name="answer"
                        value={option}
                        checked={answers[questionId] === option}
                        onChange={(e) => handleAnswer(questionId, e.target.value)}
                      />
                      <span className="option-text">
                        <span className="option-content">{optionText}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="essay-input">
                <textarea
                  value={answers[questionId] || ''}
                  onChange={(e) => handleAnswer(questionId, e.target.value)}
                  placeholder={t('quiz.pleaseEnterAnswer')}
                  rows={6}
                  className="form-input essay-textarea"
                />
                <div className="essay-tips">
                  <small>💡 {t('quiz.essayTip') || 'Share your thoughts honestly and specifically.'}</small>
                </div>
              </div>
            )}

            <div className="question-actions">
              <button onClick={checkAnswer} className="btn btn-primary btn-lg">
                {t('quiz.submitAnswer')}
              </button>
            </div>
          </>
        ) : (
          <div className="feedback-section">
            <div className={`feedback-result ${isCorrect ? 'correct' : 'incorrect'}`}>
              <div className="feedback-text">
                <h3>{isCorrect ? t('quiz.correct') : t('quiz.incorrect')}</h3>
                {question.question_type === 'multiple_choice' && (
                  <p>{t('quiz.correctAnswer')}: {question.correct_answer}</p>
                )}
              </div>
            </div>

            {question.explanation && (
              <div className="explanation">
                <h4>📚 {t('quiz.explanation')}</h4>
                <p>{question.explanation}</p>
              </div>
            )}

            <div className="question-stats">
              <div className="stat-item">
                <span className="stat-label">{t('quiz.timeElapsed')}</span>
                <span className="stat-value">
                  {questionStartTime ? formatTime(Math.floor((currentTime - questionStartTime) / 1000)) : '0:00'}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('quiz.hint')}</span>
                <span className="stat-value">{questionHints.length}/3</span>
              </div>
            </div>

            <div className="feedback-actions" style={{ marginTop: '20px' }}>
              <button onClick={nextQuestion} className="btn btn-primary btn-lg">
                {currentQuestion + 1 < questions.length ? t('quiz.nextQuestion') : t('quiz.viewResults')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage; 